import { IconButton, ToggleButton, Typography } from '@mui/material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { enumFilterOptions, systemValueCodeLabel, systemValueLabel } from '../generic/systemValues/systemValueLabel'
import { QUALITY_INCIDENCE_PRIORITY_LABEL_KEYS } from '../Mos/Widgets/qualityIncidences/qualityIncidencePriority'
import SmartMateriaUITable from '../generic/SmartMateriaUITable'
import { useToggle } from '../generic/hooks/useToggle'
import { apiPost, apiPut } from '../generic/Api_Functions'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import QualityIncidenceForm from './QualityIncidenceForm'
import EditIcon from '@mui/icons-material/Edit'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import QualityIncidenceActionsTable from './QualityIncidenceActionsTable'
import { useCallback, useRef, useState } from 'react'
import Document from '../configuration/document/Document'
import { TextSnippetOutlined } from '@mui/icons-material'
import { useAuthorization } from '../generic/hooks/useAuthorization'
import { QualityIncidenceWidget } from '../Mos/Widgets/qualityIncidences/QualityIncidenceWidgetProvider'
import useRepositorySelection from '../generic/hooks/useRepositorySelection'
import { parentPreselectedRecordFrom, childPreselectedRecordFrom, childPreselectedRecordForRow } from '../generic/utilities/repositorySelectionMapping'

const Quality = () => {
  const { t_ } = useSmartTranslation()
  const [showAllValues, setShowAllValues] = useToggle()
  const [showForm, setShowForm] = useToggle()
  const [showDocument, setShowDocument] = useToggle()
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [tableReloadNonce, setTableReloadNonce] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const formRef = useRef()
  const [openActionForm, setOpenActionForm] = useState(false)
  const isUserAuthorized = useAuthorization()
  // Historia 170 (J4b, S89/S103): QUALITY_INCIDENCE -> (child) QUALITY_INCIDENCE_ACTION. Both are
  // `RecordIdentityKind.UUID` (03-dominio.md), never `NUMERIC` like every other window here — `id`
  // travels as text end to end, `{ numeric: false }` throughout.
  const repositorySelection = useRepositorySelection()
  const handleQualityIncidencePreselectionResolved = (row) => {
    if (row && childPreselectedRecordFrom(repositorySelection, { numeric: false })) {
      setSelectedRow(row)
      setOpenActionForm(true)
    }
  }

  const columns = [
    {
      name: t_('Title'),
      field: 'title'
    },
    {
      name: t_('Code'),
      field: 'code'
    },
    {
      name: t_('Description'),
      field: 'description'
    },
    {
      name: t_('Status'),
      field: 'status',
      render: (row) => systemValueLabel(t_, 'QualityIncidenceStatusEnum', row.status),
      filterByLabel: { options: enumFilterOptions(t_, 'QualityIncidenceStatusEnum') }
    },
    {
      name: t_('Create Date'),
      field: 'createdDate',
      type: 'datetime'
    },
    {
      name: t_('End Date'),
      field: 'endDate',
      type: 'datetime',
      hide: !showAllValues
    },
    {
      name: t_('Priority'),
      field: 'priority',
      render: (row) => systemValueCodeLabel(t_, 'QualityIncidencePriorityEnum', row.priority, QUALITY_INCIDENCE_PRIORITY_LABEL_KEYS)
    }
  ]

  const actions = {
    create: {
    },
    custom: [
      {
        name: 'Edit',
        onClick: (row) => { setShowForm(); setSelectedRow(row) },
        render: (row) => {
          return (
            <IconButton>
              <EditIcon />
            </IconButton>
          )
        }
      },
      {
        name: 'Actions',
        onClick: (row) => { setSelectedRow(row); setOpenActionForm(true) },
        render: (row) => {
          return (
            <IconButton>
              <PendingActionsIcon />
            </IconButton>
          )
        }
      },
      {
        name: 'Documents',
        onClick: (row) => { setShowDocument(true); setSelectedRow(row) },
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        disabled: (row) => !isUserAuthorized('DOCUMENTS_QUALITY_INCIDENCE')
      }
    ]
  }

  const dataFetch = useCallback(async (pagination) => {
    const data = await apiPost('qualityIncidence/' + showAllValues, pagination)
    return data
  }, [showAllValues])

  const toolbar = {
    render: {
      fetchFunction: () => {
        return (
          <ToggleButton
            selected={showAllValues}
            onChange={() => setShowAllValues()}
            color='success'
            value='ALL'
            aria-label='ALL'
          >
            {t_('SHOW ALL')}
          </ToggleButton>
        )
      }
    }
  }

  const onAcceptForm = async (data) => {
    return await apiPut('qualityIncidence', data)
  }

  return (
    <>
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={
          <Document
            reference={{ id: selectedRow?.id, code: selectedRow?.code }}
            resourceType='QUALITY_INCIDENCE'
            isDownloadingFile={isDownloadingFile}
            setIsDownloadingFile={setIsDownloadingFile}
            defaultName={selectedRow?.title}
          />
        }
        setOpen={showDocument}
      />
      <SmartDialog
        closeCallback={() => setOpenActionForm(false)}
        accept={false}
        cancel={false}
        close
        setOpen={openActionForm}
        renderComponent={
          <QualityIncidenceActionsTable
            endpointType='qualityIncidence'
            formType='qualityIncidence'
            row={selectedRow}
            tableKey='qualityIncidenceActions'
            preselectedRecord={childPreselectedRecordForRow(repositorySelection, selectedRow?.id, { numeric: false })}
          />
        }
      />
      <SmartMateriaUITable
        tableTuneUniqueKey='qualityIncidence'
        title={t_('Quality Incidences')}
        columns={columns}
        dataFetch={dataFetch}
        forceLoad={`${showAllValues}-${tableReloadNonce}`}
        actions={actions}
        toolbar={toolbar}
        pageable
        preselectedRecord={parentPreselectedRecordFrom(repositorySelection, { numeric: false })}
        onPreselectionResolved={handleQualityIncidencePreselectionResolved}
        onAddElementClick={() => setShowCreateForm(true)}
        customAddRecordClick
      />
      <QualityIncidenceWidget
        createDialogOpen={showCreateForm}
        onCreateDialogClose={() => setShowCreateForm(false)}
        onSaved={() => setTableReloadNonce((prev) => !prev)}
        manualContext
        hideTrigger
      />
      <SmartDialog
        title={{ icon: <EditIcon />, render: <Typography variant='h4'>{t_('Quality Incidence edit')}</Typography> }}
        setOpen={showForm}
        cancelCallback={() => setShowForm()}
        acceptCallback={async () => {
          const success = await formRef.current?.submit()
          if (success) {
            setShowForm()
            setTableReloadNonce((prev) => !prev)
          }
        }}
        renderComponent={
          <QualityIncidenceForm
            ref={formRef}
            qualityIncidenceUuid={selectedRow?.id}
            onSubmit={(data) => onAcceptForm(data)}
            entryMode='DEFAULT'
          />
        }
      />
    </>
  )
}
export default Quality
